<script>
	// libraries

	// components
	import TempChart from "./subcharts/TempChart.svelte";
	import Buttons from "./components/Buttons.svelte";
	import Select from "./components/Select.svelte";

	// other
	import {
		lastYear,
		monthNamesFull,
		today,
		previousMonth,
		currentMonth,
		currentMonthIndex,
		currentYear,
		animationParameters,
		enoughDataForCurrentMonth,
	} from "./constants";
	import {
		stringToDate,
		formatDayISO,
		frmtTemperature,
		frmtTemperatureShort,
		frmtAnomaly,
		px,
		frmtMonthWithLatestYear,
		frmtMonthWithLatestYearShort,
		noWrap,
		getLatestYearForMonth,
		createNumberArray,
	} from "./utils";

	import {
		selectedMode,
		selectedBaseline,
		selectedMonthIndex,
		selectedMonthColor,
		lastYearColor,
		selectedMonthTextColor,
		lastYearTextColor,
	} from "./stores";

	// icons
	import emojiExtremeCold from "./icons/sehr_kalt.svg";
	import emojiVeryCold from "./icons/relativ_kalt.svg";
	import emojiNormal from "./icons/relativ_normal.svg";
	import emojiVeryWarm from "./icons/relativ_warm.svg";
	import emojiExtremeWarm from "./icons/sehr_warm.svg";

	// data
	import forecast from "./data_live/forecast.csv";
	import quintilesMonths from "./data_live/quintiles_months.json";
	import quintilesYears from "./data_live/quintiles_years.json";
	import baselineData from "./data_live/data_timespans.json";
	import anomaliesMonths from "./data_live/anomalies_months.json";
	import anomaliesYears from "./data_live/anomalies_years.json";

	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////
	const months = monthNamesFull;
	const baselines = Object.keys(baselineData);

	if (!enoughDataForCurrentMonth) $selectedMonthIndex = previousMonth.getMonth();

	const ratings = [
		{ id: "extreme_cold", text: "sehr kalt", emoji: emojiExtremeCold },
		{ id: "very_cold", text: "relativ kalt", emoji: emojiVeryCold },
		{ id: "normal", text: "relativ normal", emoji: emojiNormal },
		{ id: "very_warm", text: "relativ warm", emoji: emojiVeryWarm },
		{ id: "extreme_warm", text: "sehr warm", emoji: emojiExtremeWarm },
	];

	const labelsMonths = {
		Januar: [1940, 1975],
		Februar: [1929, 1990],
		März: [1883, 1989],
		April: [1917, 2018],
		Mai: [1917, 1902, 1991],
		Juni: [1917, 1923],
		Juli: [1919, 2006],
		August: [1956, 1911],
		September: [1912, 1947],
		Oktober: [1905, 2001],
		November: [1921, 1963],
		Dezember: [1890, 2015],
	};

	const labelsYears = [1940, 1934];

	const ticks = [1881, 1900, 1920, 1940, 1960, 1980, 2000];
	const mobileTicks = [1881, 1950];
	$: ticksMonths = [...ticks, latestYearForSelectedMonth];
	const ticksYears = [...ticks, lastYear];
	$: ticksMobileMonths = [...mobileTicks, latestYearForSelectedMonth];
	const ticksMobileYears = [...mobileTicks, lastYear];

	const marginLeft = 130;
	const marginRight = 45;

	const firstYear = 1881;

	// ///////////////////////////////////////////////////////////
	// process data
	// ///////////////////////////////////////////////////////////
	// set current year for baselines if no end year is given
	Object.values(baselineData).forEach((d) => {
		if (!d.timespan[1]) d.timespan[1] = currentYear;
	});

	// process monthly anomalies
	anomaliesMonths.forEach((m) => {
		m.dateString = m.date;
		m.date = stringToDate(m.dateString, "yyyy-mm-dd", "-");
		m.monthIndex = m.date.getMonth();

		baselines.forEach((b) => {
			m[`baseline_${b}`] = +m[`baseline_${b}`];
		});

		m.timeline.forEach((d) => {
			d.dateString = d.date;
			d.date = stringToDate(d.dateString, "yyyy-mm-dd", "-");
			d.month = m.month;
			d.monthIndex = d.date.getMonth();
			d.year = d.date.getFullYear();

			baselines.forEach((b) => {
				d[`anomaly_${b}`] = +d[`anomaly_${b}`];
			});
		});
	});

	// process yearly anomalies
	baselines.forEach((b) => {
		anomaliesYears[`baseline_${b}`] = +anomaliesYears[`baseline_${b}`];
	});

	anomaliesYears.timeline.forEach((d) => {
		d.dateString = d.date;
		d.date = stringToDate(d.dateString, "yyyy-mm-dd", "-");
		d.year = +d.year;

		baselines.forEach((b) => {
			d[`anomaly_${b}`] = +d[`anomaly_${b}`];
		});
	});

	// process forecast data
	forecast.forEach((d) => {
		Object.entries(d).forEach(([k, v]) => {
			if (k === "date") return;
			d[k] = +v;
		});
		d.dateString = d.date;
		d.date = stringToDate(d.dateString, "yyyy-mm-dd", "-");
	});

	// ///////////////////////////////////////////////////////////
	// Data for Dropdowns
	// ///////////////////////////////////////////////////////////
	const monthsDropdown = months.map((m, i) => ({
		value: i,
		label: frmtMonthWithLatestYear(i),
	}));

	const baselinesDropdown = Object.entries(baselineData).map(([id, d]) => ({
		value: id,
		label: d.label_text,
	}));

	let selectedMonthDropdown = monthsDropdown[$selectedMonthIndex];
	let selectedBaselineDropdown = baselinesDropdown.find((d) => d.value === $selectedBaseline);

	$: $selectedMonthIndex = selectedMonthDropdown.value;
	$: $selectedBaseline = selectedBaselineDropdown.value;

	// ///////////////////////////////////////////////////////////
	// Data for today
	// ///////////////////////////////////////////////////////////
	const forecastToday = forecast.find((d) => d.dateString === formatDayISO(today));
	const averageToday = forecastToday.average;
	const highestToday = forecastToday.highest;

	const quintilesCurrentMonthAverages = [...quintilesMonths[currentMonth].averages, Infinity];
	const quintileToday = quintilesCurrentMonthAverages.findIndex((q, i) => averageToday < q);
	const ratingToday = ratings[quintileToday];

	// ///////////////////////////////////////////////////////////
	// Data for month
	// ///////////////////////////////////////////////////////////
	$: selectedMonthData = anomaliesMonths
		.find((m) => m.month === selectedMonth)
		.timeline.filter((d) => d.year <= latestYearForSelectedMonth);

	$: latestYearForSelectedMonth = getLatestYearForMonth($selectedMonthIndex);
	$: latestMonthData = selectedMonthData.find(
		(d) => d.month === selectedMonth && d.year === latestYearForSelectedMonth
	);

	// anomaly
	$: anomalySelectedMonth = latestMonthData[`anomaly_${$selectedBaseline}`];
	$: absoluteAnomalySelectedMonth = Math.abs(anomalySelectedMonth);
	$: isColderSelectedMonth = anomalySelectedMonth < 0;

	// quintiles
	$: quintilesCurrentMonthAnomalies = [...quintilesMonths[selectedMonth][`anomalies_${$selectedBaseline}`], Infinity];
	$: quintileSelectedMonth = quintilesCurrentMonthAnomalies.findIndex((q, i) => anomalySelectedMonth < q);
	$: ratingSelectedMonth = ratings[quintileSelectedMonth];

	// ///////////////////////////////////////////////////////////
	// Data for year
	// ///////////////////////////////////////////////////////////
	const latestDataYear = anomaliesYears.timeline.find((d) => d.year === lastYear);

	// anomaly
	$: anomalyLastYear = latestDataYear[`anomaly_${$selectedBaseline}`];
	$: absoluteAnomalyLastYear = Math.abs(anomalyLastYear);
	$: isColderLastYear = anomalyLastYear < 0;

	// quintiles
	$: quintilesLastYearAnomalies = [...quintilesYears[`anomalies_${$selectedBaseline}`], Infinity];
	$: quintileLastYear = quintilesLastYearAnomalies.findIndex((q, i) => anomalyLastYear < q);
	$: ratingLastYear = ratings[quintileLastYear];

	// ///////////////////////////////////////////////////////////
	// Data for TempChart
	// ///////////////////////////////////////////////////////////
	$: selectedMonth = months[$selectedMonthIndex];
	$: selectedBaselineTimespan = [
		baselineData[$selectedBaseline].timespan[0],
		baselineData[$selectedBaseline].timespan[1] === currentYear
			? $selectedMode === "months"
				? latestYearForSelectedMonth
				: lastYear
			: baselineData[$selectedBaseline].timespan[1],
	];

	$: dummyMonth = {
		average: 0,
		anomaly_all: 0,
		anomaly_preindustrial: 0,
		anomaly_meterological: 0,
		year: currentYear,
		monthIndex: $selectedMonthIndex,
		month: selectedMonth,
	};

	const dummyYear = {
		anomaly_all: 0,
		anomaly_preindustrial: 0,
		anomaly_meterological: 0,
		year: currentYear,
	};

	$: chartData =
		$selectedMode === "months"
			? latestYearForSelectedMonth === currentYear
				? selectedMonthData
				: [...selectedMonthData, dummyMonth]
			: [...anomaliesYears.timeline, dummyYear];

	// ///////////////////////////////////////////////////////////
	// Data for buttons
	// ///////////////////////////////////////////////////////////
	const modes = [
		{ labelMain: "Monatsvergleich", value: "months" },
		{ labelMain: "Jahresvergleich", value: "years", labelSub: lastYear },
	];

	$: modes[0] = {
		...modes[0],
		labelSub: frmtMonthWithLatestYearShort($selectedMonthIndex),
		dataValue: frmtAnomaly(anomalySelectedMonth),
	};
	$: modes[1] = {
		...modes[1],
		dataValue: frmtAnomaly(anomalyLastYear),
	};

	// ///////////////////////////////////////////////////////////
	// DEBUG
	// ///////////////////////////////////////////////////////////

	// console.log("anomalies months", anomaliesMonths);
	// console.log("anomalies years", anomaliesYears);
	// $: console.log("selected mode", $selectedMode);
	// $: console.log("selected month index", $selectedMonthIndex);
	// $: console.log("selected baseline", $selectedBaseline);
	// $: console.log("selected month", selectedMonth);
	// $: console.log("selected month data", selectedMonthData);
</script>

<div class="temperatures">
	<div class="text-container">
		<div class="text">
			<b>Heute</b> bis zu <b>{@html noWrap(frmtTemperatureShort(highestToday))}</b> in Deutschland, im
			<span class="nowrap">Tagesmittel {@html frmtTemperatureShort(averageToday)}:</span>
			<span class="linebreak-today-desktop"><br /></span>
			<b>
				<img class="emoji" src={ratingToday.emoji} alt="" />
				{ratingToday.text}
			</b>
			<span class="linebreak-today-mobile"><br /></span>
			für einen Tag im {currentMonth}<br />

			{#if $selectedMode === "months"}
				Der
				<Select bind:selectedValue={selectedMonthDropdown} items={monthsDropdown} disabled={false} />

				{$selectedMonthIndex === currentMonthIndex && latestYearForSelectedMonth === currentYear ? "ist bisher" : `war`}
				<span class="anomaly" style:background-color={$selectedMonthColor} style:color={$selectedMonthTextColor}
					>{@html frmtTemperature(absoluteAnomalySelectedMonth)}
					{isColderSelectedMonth ? "kälter" : "wärmer"}</span
				>
			{:else}
				Das Jahr
				{lastYear} war
				<span class="anomaly" style:background-color={$lastYearColor} style:color={$lastYearTextColor}
					>{@html frmtTemperature(absoluteAnomalyLastYear)}
					{isColderLastYear ? "kälter" : "wärmer"}</span
				>
			{/if}
			<br />
			<Select bind:selectedValue={selectedBaselineDropdown} items={baselinesDropdown} disabled={false} />
		</div>
	</div>

	<div class="chart-container">
		<div class="tempchart">
			<div
				class="tempchart-container"
				style:margin-left={px(marginLeft)}
				style:margin-right={px(marginRight)}
				style:width="calc(100% - {marginLeft + marginRight}px)"
			>
				<TempChart
					data={chartData}
					xDomain={createNumberArray(firstYear, $selectedMode === "months" ? latestYearForSelectedMonth : lastYear)}
					yKey="anomaly_{$selectedBaseline}"
					zKey="anomaly_{$selectedBaseline}"
					labels={$selectedMode === "months" ? labelsMonths[selectedMonth] : labelsYears}
					formatTick={(tick, i) => tick}
					ticks={$selectedMode === "months" ? ticksMonths : ticksYears}
					mobileTicks={$selectedMode === "months" ? ticksMobileMonths : ticksMobileYears}
					baselineLabelMain="Durchschnitts-<br>{$selectedMode === 'months' ? `Temp. ${selectedMonth},` : 'Temperatur'}"
					baselineLabelSub="{selectedBaselineTimespan[0]} - {selectedBaselineTimespan[1]}"
					selectedMonth={$selectedMode === "months" ? selectedMonth : ""}
					{marginLeft}
					{marginRight}
					highlight={selectedBaselineTimespan}
				/>
			</div>
		</div>
	</div>

	<div class="buttons-container">
		<Buttons bind:value={$selectedMode} options={modes} let:option let:active>
			<div class="main" data-title={option.labelMain}>
				{@html option.labelMain}
			</div>
			<div class="sub">
				<div class="sub-text">{@html option.labelSub}</div>
				<div
					class="anomaly"
					style:background-color={!active ? null : option.value === "months" ? $selectedMonthColor : $lastYearColor}
					style:color={!active ? null : option.value === "months" ? $selectedMonthTextColor : $lastYearTextColor}
				>
					<div>{@html option.dataValue}</div>
				</div>
			</div>
		</Buttons>
	</div>
</div>

<style lang="scss">
	@import "./main.scss";

	.temperatures {
		font-family: $ff-sans;
		color: $black;
		width: 100%;
		height: 100%;
		max-width: 940px;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		align-items: center;
		row-gap: 20px;
		overflow: hidden;
		box-sizing: border-box;

		@media (max-width: 960px) {
			padding: 0 10px;
		}
	}

	.nowrap {
		white-space: nowrap;
	}

	b {
		font-weight: $medium;
	}

	.emoji {
		position: relative;
	}

	.chart-container {
		width: 100%;
		height: 100%;
		flex-grow: 1;
		display: flex;
		font-size: $fs-chart-l;
		line-height: $lh-chart-l;

		.tempchart {
			width: 100%;
			display: flex;
			flex-direction: column;
			flex-grow: 1;

			.tempchart-container {
				height: 100%;
				flex-grow: 1;
				flex-shrink: 0;
			}
		}
	}

	.baseline-label {
		align-self: flex-start;
		font-size: $fs-chart-l;
		line-height: $lh-chart-l;
		opacity: 0.7;

		span {
			font-weight: $medium;
		}
	}

	.text-container {
		padding: 5px;
		box-sizing: border-box;

		.text {
			letter-spacing: 0.01em;
			background: $white;
			box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.15);
			border-radius: 8px;
			box-sizing: border-box;

			.anomaly {
				border-radius: 5px;
				padding: 2px 6px;
			}
		}
	}

	.buttons-container {
		letter-spacing: 0.01em;

		.main {
			margin-bottom: 5px;
			font-size: 22px;
			line-height: 150%;

			&::after {
				content: attr(data-title);
				display: block;
				font-weight: 500;
				height: 0px;
				overflow: hidden;
				color: transparent;
				pointer-events: none;
				visibility: hidden;
			}
		}

		.sub {
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			font-weight: $medium;
			font-size: $fs-chart-l;
			line-height: $lh-chart-l;
		}
		.sub-text {
			margin-right: 4px;
		}
	}

	$linebreak: 478px;

	@media (max-width: $linebreak) {
		.linebreak-today-desktop {
			display: none;
		}
	}

	@media (min-width: #{$linebreak + 1px}) {
		.linebreak-today-mobile {
			display: none;
		}
	}
	// <= large tablets
	@media (max-width: $breakpoint-tablet-l) {
		.tempchart-container {
			// margin-right: 0 !important;
		}
	}

	// > small tablets
	@media (min-width: #{$breakpoint-tablet-s + 1px}) {
		.text {
			padding: 20px 37px 25px 37px;
		}

		.baseline-label {
			display: none;
		}

		.emoji {
			width: 23px;
			top: 4px;
		}

		.tempchart-container {
			flex-basis: 410px;
		}

		.buttons-container {
			margin-top: 20px;
		}
	}

	// very small phones - small tablets
	@media (min-width: #{$breakpoint-mobile-xs + 1px}) and (max-width: $breakpoint-tablet-s) {
		.text {
			padding: 10px 13px 20px 13px;
		}
	}

	// <= very small phones
	@media (max-width: $breakpoint-mobile-xs) {
		.text {
			padding: 10px 10px 20px 10px;
		}
	}

	// <= small tablets
	@media (max-width: $breakpoint-tablet-s) {
		.tempchart-container {
			flex-basis: 320px;
		}

		.tempchart-container {
			width: calc(100%) !important;
			margin-left: 0 !important;
			margin-right: 0 !important;
		}
		.emoji {
			width: 19px;
			top: 2px;
		}
	}

	// > large/normal phones
	@media (min-width: #{$breakpoint-mobile-l + 1px}) {
		.temperatures {
			font-size: $fs-desktop;
			line-height: $lh-desktop;
		}
	}

	// <= large/normal phones
	@media (max-width: $breakpoint-mobile-l) {
		.temperatures {
			font-size: $fs-mobile;
			line-height: $lh-mobile;
		}

		.buttons-container {
			.main {
				font-size: 20px;
				line-height: 150%;
			}
		}
	}

	// desktop
	@media (min-width: #{$breakpoint-tablet-l + 1px}) {
	}

	// large tablets
	@media (min-width: #{$breakpoint-tablet-s + 1px}) and (max-width: $breakpoint-tablet-l) {
	}

	// small tablets
	@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-s) {
	}

	// large/normal phones
	@media (min-width: #{$breakpoint-mobile-s + 1px}) and (max-width: $breakpoint-mobile-l) {
	}

	// small phones
	@media (min-width: #{$breakpoint-mobile-xs + 1px}) and (max-width: $breakpoint-mobile-s) {
	}

	// verz small phones
	@media (max-width: $breakpoint-mobile-xs) {
	}

	// small phones
	@media (max-width: $breakpoint-mobile-s) {
		.temperatures {
			font-size: calc(100vw / 23);
		}

		.buttons-container {
			.main {
				font-size: calc(100vw / 23);
			}
		}

		.text-container {
			width: calc(100% + 20px);
			padding: 8px 8px;
			height: calc(48vw + 16px);
		}

		.text {
			height: 100%;
			padding: 10px 5px 20px 10px;
		}
	}
</style>
